/**
 * @fileoverview Demo Request Dialog - Modal form for requesting product demos
 *
 * Purpose: Captures lead information for demo requests
 * Features:
 * - Form validation with React Hook Form + Zod
 * - Convex mutation for data persistence
 * - Toast notifications for success/error
 * - Accessible modal dialog
 *
 * Used by: Hero section, CTA section, navigation buttons
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import { ArrowRight } from "lucide-react";
import { usePostHog } from "@/utils/posthog";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/composed";
import { Input } from "@/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives";
import { Button } from "@/ui/primitives";
import { Logo } from "@/ui/layout";
import { toast } from "sonner";

/**
 * Form validation schema
 *
 * Validates:
 * - Email format
 * - Required name (min 2 chars)
 * - Required company name (min 2 chars)
 * - Organisation type selection
 */
const demoRequestSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  organizationType: z.string().min(1, "Please select an organisation type"),
});

type DemoRequestFormValues = z.infer<typeof demoRequestSchema>;

/**
 * Organisation types for the dropdown
 */
const ORGANIZATION_TYPES = [
  { value: "law-firm", label: "Law Firm" },
  { value: "insolvency-practice", label: "Insolvency Practice" },
  { value: "restructuring-advisory", label: "Restructuring Advisory" },
  { value: "financial-services", label: "Financial Services" },
  { value: "corporate", label: "Corporate" },
  { value: "other", label: "Other" },
] as const;

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Demo Request Dialog Component
 *
 * Modal dialog with form for capturing demo request information.
 * Submits to Convex backend and shows success/error toasts.
 *
 * @param open - Whether the dialog is open
 * @param onOpenChange - Callback to control dialog open state
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <Button onClick={() => setIsOpen(true)}>Request a demo</Button>
 * <DemoRequestDialog open={isOpen} onOpenChange={setIsOpen} />
 */
export function DemoRequestDialog({
  open,
  onOpenChange,
}: DemoRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const createDemoRequest = useMutation(api.demo.requests.create);
  const posthog = usePostHog();

  const form = useForm<DemoRequestFormValues>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: {
      email: "",
      name: "",
      companyName: "",
      organizationType: "",
    },
  });

  const onSubmit = async (data: DemoRequestFormValues) => {
    setIsSubmitting(true);

    try {
      await createDemoRequest({
        email: data.email,
        name: data.name,
        companyName: data.companyName,
        organizationType: data.organizationType,
      });

      // Track successful demo request submission
      posthog?.capture("request_submitted", {
        organization_type: data.organizationType,
        has_company_name: !!data.companyName,
      });

      // Close form dialog and show confirmation
      onOpenChange(false);
      form.reset();
      setShowConfirmation(true);
    } catch (error) {
      // Track failed submission
      posthog?.capture("demo_request_failed", {
        error_type: "submission_error",
      });

      toast.error("Failed to submit request", {
        description: "Please try again or contact us directly.",
      });
      console.error("Demo request error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">See Morgan in action</DialogTitle>
            <DialogDescription>
              Fill in your details &amp; we'll get in touch to schedule a
              personalised demo.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="you@company.com"
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your company" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="organizationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organisation type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select organisation type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ORGANIZATION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full gap-2"
                size="lg"
                expandIcon
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Book a demo"}
                {!isSubmitting && <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/10">
              <Logo compact width={60} height={60} className="text-primary" />
            </div>

            <div className="space-y-2">
              <DialogTitle className="text-2xl font-semibold">
                Thanks for your interest!
              </DialogTitle>
              <DialogDescription className="text-base">
                The team will be in touch shortly to schedule your personalised
                demo.
              </DialogDescription>
            </div>

            <Button
              onClick={() => setShowConfirmation(false)}
              className="mt-4 w-full"
              size="lg"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
